/**
 * Stable stringify for React Query keys
 * 
 * Guarantees deterministic string output for arrays and objects,
 * ensuring that logically equivalent structures produce the same key.
 * 
 * Features:
 * - Stable object key ordering (alphabetical)
 * - Stable array element ordering for filter arrays
 * - Handles nested structures (arrays of arrays, objects with arrays, etc.)
 * 
 * @param value - Value to stringify (array, object, or primitive)
 * @returns Deterministic string representation
 */
export function stableStringify(value: any): string {
  if (value === null) {
    return "null";
  }

  if (value === undefined) {
    return "undefined";
  }

  if (typeof value !== "object") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    // Treat arrays-of-3/4-tuples as "filter arrays" for stable ordering.
    // This is designed for Frappe filters / or_filters, not arbitrary 2D arrays.
    const isFilterArray = value.length > 0 && 
      Array.isArray(value[0]) && 
      (value[0].length === 3 || value[0].length === 4);

    if (isFilterArray) {
      // ✅ Sort filter rows for stable key generation
      // Sort by: [doctype, field, operator, valueAsString]
      const sorted = [...value].sort((a, b) => {
        // Compare doctype (index 0 if length 4, otherwise field is at 0)
        const aDoctype = a.length >= 4 ? String(a[0]) : "";
        const bDoctype = b.length >= 4 ? String(b[0]) : "";
        if (aDoctype !== bDoctype) return aDoctype.localeCompare(bDoctype);

        // Compare field (index 1 if length 4, otherwise index 0)
        const aField = a.length >= 4 ? String(a[1]) : String(a[0]);
        const bField = b.length >= 4 ? String(b[1]) : String(b[0]);
        if (aField !== bField) return aField.localeCompare(bField);

        // Compare operator (index 2 if length 4, otherwise index 1)
        const aOp = a.length >= 4 ? String(a[2]) : String(a[1]);
        const bOp = b.length >= 4 ? String(b[2]) : String(b[1]);
        if (aOp !== bOp) return aOp.localeCompare(bOp);

        // Compare value (index 3 if length 4, otherwise index 2)
        const aValue = a.length >= 4 ? a[3] : a[2];
        const bValue = b.length >= 4 ? b[3] : b[2];
        return JSON.stringify(aValue).localeCompare(JSON.stringify(bValue));
      });

      return JSON.stringify(sorted);
    }

    // Regular array - recursively stringify elements
    return `[${value.map(item => stableStringify(item)).join(",")}]`;
  }

  // Object - sort keys alphabetically
  const sortedKeys = Object.keys(value).sort();
  const sortedObj: Record<string, any> = {};
  for (const key of sortedKeys) {
    sortedObj[key] = value[key];
  }

  // Recursively stringify values
  const entries = Object.entries(sortedObj).map(([k, v]) => {
    return `"${k}":${stableStringify(v)}`;
  });

  return `{${entries.join(",")}}`;
}

